export interface OPFSFile {
  id: string;
  name: string;
  size: number;
  type: string;
  lastModified: number;
  handle: FileSystemFileHandle;
}

export class OPFSManager {
  private static instance: OPFSManager;
  private directoryHandle: FileSystemDirectoryHandle | null = null;

  private constructor() {}

  public static getInstance(): OPFSManager {
    if (!OPFSManager.instance) {
      OPFSManager.instance = new OPFSManager();
    }
    return OPFSManager.instance;
  }

  public static isSupported(): boolean {
    return (
      'navigator' in globalThis &&
      'storage' in navigator &&
      'getDirectory' in navigator.storage
    );
  }

  private async getDirectoryHandle(): Promise<FileSystemDirectoryHandle> {
    if (!this.directoryHandle) {
      if (!OPFSManager.isSupported()) {
        throw new Error('OPFS is not supported in this browser');
      }
      this.directoryHandle = await navigator.storage.getDirectory();
    }
    return this.directoryHandle;
  }

  public async storeFile(file: File): Promise<OPFSFile> {
    if (!file || !(file instanceof File)) {
      throw new Error('Invalid file object provided');
    }

    const directoryHandle = await this.getDirectoryHandle();
    const fileId = `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
    // Sanitize filename to prevent path traversal
    const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const fileName = `${fileId}-${safeName}`;

    let writable: FileSystemWritableFileStream | null = null;
    try {
      const fileHandle = await directoryHandle.getFileHandle(fileName, {
        create: true,
      });
      writable = await fileHandle.createWritable();
      await writable.write(file);
      await writable.close();
      writable = null;

      return {
        id: fileId,
        name: file.name,
        size: file.size,
        type: file.type,
        lastModified: file.lastModified,
        handle: fileHandle,
      };
    } catch (error) {
      // Ensure writable stream is closed on error
      if (writable) {
        try {
          await writable.abort();
        } catch (abortError) {
          console.warn('Failed to abort writable stream:', abortError);
        }
      }
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      throw new Error(`Failed to store file in OPFS: ${errorMessage}`);
    }
  }

  public async deleteFile(fileId: string): Promise<boolean> {
    try {
      const directoryHandle = await this.getDirectoryHandle();

      // Find and delete the file with matching ID
      for await (const [name, handle] of (directoryHandle as any).entries()) {
        if (name.startsWith(fileId) && handle.kind === 'file') {
          await directoryHandle.removeEntry(name);
          return true;
        }
      }
      return false;
    } catch (error) {
      return false;
    }
  }

  public async clearAll(): Promise<void> {
    try {
      const directoryHandle = await this.getDirectoryHandle();

      for await (const [name, handle] of (directoryHandle as any).entries()) {
        if (handle.kind === 'file') {
          await directoryHandle.removeEntry(name);
        }
      }
    } catch (error) {
      throw new Error(`Failed to clear OPFS: ${error}`);
    }
  }
}
